import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  Loader2,
  Eye,
  Store,
  CreditCard,
  Wine,
  AlertCircle,
  RefreshCw,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
  Percent
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { getFullImageUrl } from '../../utils/images';
import { getApiErrorMessage } from '../../utils/errorHelper';

// Configuration des types de commerce
const SHOP_TYPE_LABELS = {
  restaurant_traditionnel: 'Restaurant traditionnel',
  restaurant_rapide: 'Restaurant rapide',
  epicerie: 'Épicerie',
  boulangerie: 'Boulangerie',
  boucherie: 'Boucherie',
  epicerie_fine: 'Épicerie fine',
  poissonnier: 'Poissonnier',
  fromager: 'Fromager'
};

const SHOP_TYPE_COLORS = {
  restaurant_traditionnel: 'bg-orange-100 text-orange-700',
  restaurant_rapide: 'bg-yellow-100 text-yellow-700',
  epicerie: 'bg-green-100 text-green-700',
  boulangerie: 'bg-amber-100 text-amber-700',
  boucherie: 'bg-red-100 text-red-700',
  epicerie_fine: 'bg-purple-100 text-purple-700',
  poissonnier: 'bg-blue-100 text-blue-700',
  fromager: 'bg-indigo-100 text-indigo-700'
};

const STATUS_CONFIG = {
  pending_review: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700', icon: XCircle },
  live: { label: 'En ligne', color: 'bg-emerald-100 text-emerald-700', icon: Store }
};

export default function AdminOnboarding() {
  const { getAuthHeader } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending_review');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [activeTab, setActiveTab] = useState('inscriptions'); // 'inscriptions' or 'analytics'
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Charger les restaurants
  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/restaurants`, {
        headers: getAuthHeader()
      });
      setRestaurants(response.data || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      toast.error('Erreur lors du chargement des inscriptions');
    } finally {
      setLoading(false);
    }
  };

  // Charger les analytics
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/admin/analytics`, {
        headers: getAuthHeader()
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) {
      fetchAnalytics();
    }
  }, [activeTab]);

  // Filtrer les restaurants
  const filteredRestaurants = restaurants.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = !searchTerm || 
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Compter par statut
  const countByStatus = (status) => {
    return restaurants.filter(r => r.status === status).length;
  };

  // Approuver un restaurant
  const handleApprove = async (restaurantId) => {
    setActionLoading(restaurantId);
    try {
      await axios.put(
        `${API_URL}/api/onboarding/admin/${restaurantId}/approve`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Restaurant approuvé avec succès !');
      fetchRestaurants();
    } catch (error) {
      console.error('Error approving restaurant:', error);
      toast.error(getApiErrorMessage(error, "Erreur lors de l'approbation"));
    } finally {
      setActionLoading(null);
    }
  };

  // Rejeter un restaurant
  const handleReject = async (restaurantId) => {
    if (!rejectReason.trim()) {
      toast.error('Veuillez indiquer un motif de rejet');
      return;
    }
    
    setActionLoading(restaurantId);
    try {
      await axios.put(
        `${API_URL}/api/onboarding/admin/${restaurantId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Restaurant rejeté');
      setShowRejectModal(null);
      setRejectReason('');
      fetchRestaurants();
    } catch (error) {
      console.error('Error rejecting restaurant:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors du rejet'));
    } finally {
      setActionLoading(null);
    }
  };

  // Mettre en ligne
  const handleGoLive = async (restaurantId) => {
    setActionLoading(restaurantId);
    try {
      await axios.put(
        `${API_URL}/api/onboarding/admin/${restaurantId}/go-live`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Restaurant mis en ligne !');
      fetchRestaurants();
    } catch (error) {
      console.error('Error setting restaurant live:', error);
      toast.error(getApiErrorMessage(error, 'Erreur'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-onboarding">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inscriptions commerces</h1>
            <p className="text-slate-500 mt-1">Gérez les demandes d'inscription et suivez les analytics</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={activeTab === 'analytics' ? fetchAnalytics : fetchRestaurants}
              disabled={loading || loadingAnalytics}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${(loading || loadingAnalytics) ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('inscriptions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'inscriptions'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Building2 className="w-4 h-4 inline-block mr-2" />
            Inscriptions
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'analytics'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="analytics-tab"
          >
            <BarChart3 className="w-4 h-4 inline-block mr-2" />
            Analytics Drop-off
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'analytics' ? (
          <OnboardingAnalytics 
            analytics={analytics} 
            loading={loadingAnalytics}
            onRefresh={fetchAnalytics}
          />
        ) : (
          <>
            {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setFilter('pending_review')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filter === 'pending_review' 
                ? 'border-yellow-500 bg-yellow-50' 
                : 'border-slate-100 bg-white hover:border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">{countByStatus('pending_review')}</p>
                <p className="text-sm text-slate-500">En attente</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setFilter('approved')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filter === 'approved' 
                ? 'border-green-500 bg-green-50' 
                : 'border-slate-100 bg-white hover:border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">{countByStatus('approved')}</p>
                <p className="text-sm text-slate-500">Approuvés</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setFilter('live')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filter === 'live' 
                ? 'border-emerald-500 bg-emerald-50' 
                : 'border-slate-100 bg-white hover:border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Store className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">{countByStatus('live')}</p>
                <p className="text-sm text-slate-500">En ligne</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setFilter('rejected')}
            className={`p-4 rounded-xl border-2 transition-all ${
              filter === 'rejected' 
                ? 'border-red-500 bg-red-50' 
                : 'border-slate-100 bg-white hover:border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold text-slate-900">{countByStatus('rejected')}</p>
                <p className="text-sm text-slate-500">Rejetés</p>
              </div>
            </div>
          </button>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email ou ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            Tous ({restaurants.length})
          </Button>
        </div>

        {/* Liste des inscriptions */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune inscription</h3>
            <p className="text-slate-500">
              {filter === 'pending_review' 
                ? 'Aucune inscription en attente de validation'
                : 'Aucun résultat pour ce filtre'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRestaurants.map((restaurant) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                isExpanded={expandedId === restaurant.id}
                onToggle={() => setExpandedId(expandedId === restaurant.id ? null : restaurant.id)}
                onApprove={() => handleApprove(restaurant.id)}
                onReject={() => setShowRejectModal(restaurant.id)}
                onGoLive={() => handleGoLive(restaurant.id)}
                actionLoading={actionLoading === restaurant.id}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {/* Modal de rejet */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Rejeter cette inscription</h3>
                  <p className="text-sm text-slate-500">Indiquez le motif du rejet</p>
                </div>
              </div>
              
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Documents manquants, informations incomplètes..."
                className="w-full h-32 p-3 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(null);
                    setRejectReason('');
                  }}
                >
                  Annuler
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(showRejectModal)}
                  disabled={actionLoading === showRejectModal}
                  className="bg-red-500 hover:bg-red-600"
                >
                  {actionLoading === showRejectModal ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Confirmer le rejet'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// Composant carte restaurant
function RestaurantCard({ 
  restaurant, 
  isExpanded, 
  onToggle, 
  onApprove, 
  onReject, 
  onGoLive,
  actionLoading,
  formatDate 
}) {
  const statusConfig = STATUS_CONFIG[restaurant.status] || STATUS_CONFIG.pending_review;
  const StatusIcon = statusConfig.icon;
  const shopTypeLabel = SHOP_TYPE_LABELS[restaurant.shop_type] || restaurant.shop_type;
  const shopTypeColor = SHOP_TYPE_COLORS[restaurant.shop_type] || 'bg-slate-100 text-slate-700';

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Logo ou initiale */}
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center overflow-hidden">
              {restaurant.logo ? (
                <img src={getFullImageUrl(restaurant.logo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl font-bold text-emerald-600">
                  {restaurant.name?.charAt(0) || '?'}
                </span>
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{restaurant.name}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${shopTypeColor}`}>
                  {shopTypeLabel}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {restaurant.city || 'Non renseigné'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(restaurant.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.color}`}>
              <StatusIcon className="w-3.5 h-3.5" />
              {statusConfig.label}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {/* Détails étendus */}
      {isExpanded && (
        <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/50">
          {/* Informations de contact */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-500" />
                Informations du commerce
              </h4>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {restaurant.address}, {restaurant.zipcode} {restaurant.city}
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {restaurant.phone || 'Non renseigné'}
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" />
                  {restaurant.email}
                </p>
                {restaurant.siret && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <FileText className="w-4 h-4 text-slate-400" />
                    SIRET: {restaurant.siret}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-500" />
                Responsable
              </h4>
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">
                  Propriétaire: {restaurant.owner_id ? 'Associé' : 'Non renseigné'}
                </p>
              </div>
            </div>
          </div>

          {/* Moyens de paiement */}
          {restaurant.accepted_payment_methods?.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-500" />
                Moyens de paiement acceptés
              </h4>
              <div className="flex flex-wrap gap-2">
                {restaurant.accepted_payment_methods.map((method) => (
                  <span 
                    key={method}
                    className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600"
                  >
                    {method === 'card' ? 'Carte bancaire' :
                     method === 'cash' ? 'Espèces' :
                     method === 'ticket_resto_paper' ? 'Ticket resto papier' :
                     method === 'ticket_resto_card' ? 'Carte ticket resto' :
                     method === 'amex' ? 'American Express' :
                     method === 'bitcoin' ? 'Bitcoin' : method}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-500" />
              Documents
            </h4>
            <div className="flex flex-wrap gap-2">
              {restaurant.alcohol_license ? (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs flex items-center gap-1">
                  <Wine className="w-3 h-3" />
                  Licence alcool fournie
                </span>
              ) : (
                <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-lg text-xs">
                  Pas de licence alcool
                </span>
              )}
              {restaurant.shop_details?.sells_alcohol && !restaurant.alcohol_license && (
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Vend de l'alcool mais licence manquante
                </span>
              )}
            </div>
          </div>

          {/* Motif de rejet (si rejeté) */}
          {restaurant.status === 'rejected' && restaurant.rejection_reason && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-sm text-red-700">
                <strong>Motif du rejet :</strong> {restaurant.rejection_reason}
              </p>
              <p className="text-xs text-red-500 mt-1">
                Rejeté le {formatDate(restaurant.rejected_at)}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-200">
            {restaurant.status === 'pending_review' && (
              <>
                <Button
                  onClick={onApprove}
                  disabled={actionLoading}
                  className="bg-emerald-500 hover:bg-emerald-600 gap-2"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Approuver
                </Button>
                <Button
                  variant="outline"
                  onClick={onReject}
                  disabled={actionLoading}
                  className="text-red-600 border-red-200 hover:bg-red-50 gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Rejeter
                </Button>
              </>
            )}
            
            {restaurant.status === 'approved' && (
              <Button
                onClick={onGoLive}
                disabled={actionLoading}
                className="bg-emerald-500 hover:bg-emerald-600 gap-2"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Store className="w-4 h-4" />
                )}
                Mettre en ligne
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => window.open(`/restaurant/${restaurant.id}`, '_blank')}
              className="gap-2"
            >
              <Eye className="w-4 h-4" />
              Voir la page
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// Composant Analytics Drop-off
function OnboardingAnalytics({ analytics, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune donnée</h3>
        <p className="text-slate-500">Les analytics seront disponibles après les premières inscriptions</p>
      </div>
    );
  }

  const { overview, last_30_days, dropoff_by_step, shop_types_stats, step_names } = analytics;

  return (
    <div className="space-y-6" data-testid="onboarding-analytics">
      {/* KPIs Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{overview?.total_sessions || 0}</p>
              <p className="text-sm text-slate-500">Sessions totales</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{overview?.completed_sessions || 0}</p>
              <p className="text-sm text-slate-500">Complétées</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{overview?.completion_rate || 0}%</p>
              <p className="text-sm text-slate-500">Taux complétion</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {overview?.avg_completion_time_minutes ? `${Math.round(overview.avg_completion_time_minutes)}m` : '-'}
              </p>
              <p className="text-sm text-slate-500">Temps moyen</p>
            </div>
          </div>
        </div>
      </div>

      {/* 30 derniers jours */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          30 derniers jours
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-3xl font-bold text-slate-900">{last_30_days?.sessions_started || 0}</p>
            <p className="text-sm text-slate-500">Nouvelles sessions</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-3xl font-bold text-emerald-600">{last_30_days?.sessions_completed || 0}</p>
            <p className="text-sm text-slate-500">Inscriptions finalisées</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-3xl font-bold text-purple-600">{last_30_days?.completion_rate || 0}%</p>
            <p className="text-sm text-slate-500">Taux de conversion</p>
          </div>
        </div>
      </div>

      {/* Drop-off par étape */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-red-500" />
          Abandons par étape
        </h3>
        {dropoff_by_step?.length > 0 ? (
          <div className="space-y-3">
            {dropoff_by_step.map((item, index) => (
              <div key={item.step} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
                  {item.step + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 capitalize">
                      {item.step_name?.replace(/_/g, ' ') || `Étape ${item.step + 1}`}
                    </span>
                    <span className="text-sm text-slate-500">
                      {item.count} abandon{item.count > 1 ? 's' : ''} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        item.percentage > 30 ? 'bg-red-500' : 
                        item.percentage > 15 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(item.percentage, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">Aucun abandon enregistré</p>
        )}
      </div>

      {/* Stats par type de commerce */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Store className="w-5 h-5 text-emerald-500" />
          Performance par type de commerce
        </h3>
        {shop_types_stats?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-slate-500 font-medium">Type</th>
                  <th className="text-center py-2 text-slate-500 font-medium">Total</th>
                  <th className="text-center py-2 text-slate-500 font-medium">Complétées</th>
                  <th className="text-center py-2 text-slate-500 font-medium">Taux</th>
                </tr>
              </thead>
              <tbody>
                {shop_types_stats.map((stat) => (
                  <tr key={stat._id} className="border-b border-slate-50">
                    <td className="py-3 font-medium text-slate-900 capitalize">
                      {SHOP_TYPE_LABELS[stat._id] || stat._id?.replace(/_/g, ' ')}
                    </td>
                    <td className="py-3 text-center text-slate-600">{stat.total}</td>
                    <td className="py-3 text-center text-emerald-600 font-medium">{stat.completed}</td>
                    <td className="py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        stat.completion_rate >= 50 ? 'bg-emerald-100 text-emerald-700' :
                        stat.completion_rate >= 25 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {stat.completion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">Aucune donnée disponible</p>
        )}
      </div>

      {/* Sessions récupérables */}
      {analytics?.recoverable_sessions > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Activity className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-900">
              {analytics.recoverable_sessions} session{analytics.recoverable_sessions > 1 ? 's' : ''} récupérable{analytics.recoverable_sessions > 1 ? 's' : ''}
            </p>
            <p className="text-sm text-amber-700">
              Ces utilisateurs ont commencé leur inscription mais ne l'ont pas finalisée (sessions non expirées)
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
